class ProductTracker {
  constructor() {
    this.storageKey = 'recently_viewed_handles';
    this.maxStorageLimit = 20;
    this.init();
  }

  getMaxProducts() {
    const element = document.querySelector('recently-viewed-products');
    if (element && element.dataset.maxProducts) {
      return parseInt(element.dataset.maxProducts, 10);
    }
    const defaultMaxProducts = 8;
    return defaultMaxProducts;
  }

  init() {
    const handle = this.getProductHandle();
    if (handle) {
      this.addHandle(handle);
    }
  }

  getProductHandle() {
    if (window.product && window.product.handle) {
      return window.product.handle;
    }
    const pathnameParts = window.location.pathname.split('/');
    const index = pathnameParts.indexOf('products');
    if (index > -1 && pathnameParts.length > index + 1) {
      return pathnameParts[index + 1];
    }
    return null;
  }

  addHandle(handle) {
    let handles = this.getHandles();
    handles = handles.filter(h => h !== handle);
    handles.unshift(handle);
    handles = handles.slice(0, this.maxStorageLimit);
    localStorage.setItem(this.storageKey, JSON.stringify(handles));
  }

  getHandles() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      const handles = stored ? JSON.parse(stored) : [];
      
      return handles;
    } catch {
      return [];
    }
  }
}

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.storageKey = 'recently_viewed_handles';
    this.sectionId = 'component-recently-viewed-product';
    this.swiper = null;
  }

  connectedCallback() {
    this.renderRecentlyViewed();
    this.resizeHandler = () => this.handleResize();
    window.addEventListener('resize', this.resizeHandler);
    
    this.customizeHandler = (event) => this.handleCustomizerUpdate(event);
    document.addEventListener('shopify:section:load', this.customizeHandler);
    document.addEventListener('shopify:section:reorder', this.customizeHandler);
    document.addEventListener('shopify:section:select', this.customizeHandler);
    document.addEventListener('shopify:section:deselect', this.customizeHandler);
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }

    if (this.customizeHandler) {
      document.removeEventListener('shopify:section:load', this.customizeHandler);
      document.removeEventListener('shopify:section:reorder', this.customizeHandler);
      document.removeEventListener('shopify:section:select', this.customizeHandler);
      document.removeEventListener('shopify:section:deselect', this.customizeHandler);
    }
  }

  getMaxProducts() {
    if (this.dataset.maxProducts) {
      return parseInt(this.dataset.maxProducts, 10);
    }
    return 4;
  }

  getHandles() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      const handles = stored ? JSON.parse(stored) : [];
     
      return handles.slice(0, this.getMaxProducts());
    } catch {
      return [];
    }
  }

  removeHandles(handlesToRemove) {
    try {
      const stored = localStorage.getItem(this.storageKey);
      const handles = stored ? JSON.parse(stored) : [];
      const filtered = handles.filter(h => !handlesToRemove.includes(h));
      localStorage.setItem(this.storageKey, JSON.stringify(filtered));
    } catch {
      // Silently handle errors
    }
  }

  initializeSwiper() {
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.initializeSwiper(), 100);
      return;
    }

    const container = this.querySelector('.recently-viewed-products__content');
    if (!container) return;

    if (window.innerWidth > 749) return;

    container.classList.add('swiper');
    
    let wrapper = container.querySelector('.swiper-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'swiper-wrapper';
      
      const products = container.querySelectorAll('div[id*="shopify-section-component-recently-viewed-product"]');
      products.forEach(product => {
        product.classList.add('swiper-slide');
        wrapper.appendChild(product);
      });
      
      container.appendChild(wrapper);
    }

    // Add pagination
    if (!container.querySelector('.swiper-pagination')) {
      const pagination = document.createElement('div');
      pagination.className = 'swiper-pagination';
      container.appendChild(pagination);
    }

    // Initialize Swiper
    this.swiper = new Swiper(container, {
      slidesPerView: 2,
      spaceBetween: 24,
      loop: false,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
        dynamicBullets: true,
      },
      breakpoints: {
        0: {
          slidesPerView: 2,
          spaceBetween: 24,
        },
        480: {
          slidesPerView: 2,
          spaceBetween: 24,
        },
      },
      speed: 300,
      grabCursor: true,
      watchOverflow: true,
    });
  }

  destroySwiper() {
    if (this.swiper) {
      // Move slides back out of wrapper
      const wrapper = this.querySelector('.swiper-wrapper');
      const container = this.querySelector('.recently-viewed-products__content');
      
      if (wrapper && container) {
        const slides = wrapper.querySelectorAll('.swiper-slide');
        slides.forEach(slide => {
          slide.classList.remove('swiper-slide');
          container.appendChild(slide);
        });
        wrapper.remove();
      }

      // Remove pagination
      const pagination = container.querySelector('.swiper-pagination');
      if (pagination) pagination.remove();

      // Remove swiper class
      container.classList.remove('swiper');

      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  async renderRecentlyViewed() {
    const container = this.querySelector('.recently-viewed-products__content');
    if (!container) return;

    // In customizer mode, don't replace the sample products
    if (window.Shopify && window.Shopify.designMode) {
      setTimeout(() => this.initializeSwiper(), 100);
      return;
    }

    const handles = this.getHandles();

    if (handles.length === 0) {
      container.innerHTML = '<p class="recently-viewed-empty">No recently viewed products yet.</p>';
      return;
    }

    // Clear container before adding new content (only on live site)
    container.innerHTML = '';

    const fetches = handles.map(handle => {
      const url = `/products/${handle}?sections=${this.sectionId}`;
      return fetch(url)
        .then(res => {
          // Check if response is OK (200-299)
          if (!res.ok) {
            return { handle, response: null, error: 'not_ok' };
          }
          // Check if response is JSON
          const contentType = res.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            return { handle, response: null, error: 'not_json' };
          }
          return res.json()
            .then(json => ({ handle, response: json, error: null }))
            .catch(() => ({ handle, response: null, error: 'parse_error' }));
        })
        .catch(err => {
          return { handle, response: null, error: 'fetch_error' };
        });
    });

    const results = await Promise.all(fetches);
    const invalidHandles = [];

    results.forEach((result) => {
      const { handle, response, error } = result;
      
      // If fetch failed or response is invalid, mark as invalid
      if (error || !response) {
        invalidHandles.push(handle);
        return;
      }
      
      const html = response[this.sectionId];
      
      // Check if HTML exists and is not empty
      if (!html || typeof html !== 'string' || !html.trim()) {
        invalidHandles.push(handle);
        return;
      }

      // Check if HTML contains actual product content
      // If product is removed/hidden/draft, the section will only contain empty wrapper
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html.trim();
      
      // Check if the HTML contains product card elements
      // Look for specific product card structure, not just any product-related elements
      const productLink = tempDiv.querySelector('a[href*="/products/"]');
      const productImage = tempDiv.querySelector('img[src*="products"], img[src*="cdn.shopify"]');
      const productCard = tempDiv.querySelector('.product-card, [class*="product-card"]');
      const priceElement = tempDiv.querySelector('[class*="price"]:not([class*="compare"])');
      
      // If no product link or no meaningful content, it's invalid
      // Draft products might have placeholder data but won't have a valid product link
      if (!productLink || (!productImage && !productCard && !priceElement)) {
        invalidHandles.push(handle);
        return;
      }

      // Additional validation: Check if the product link href matches the expected handle
      const linkHref = productLink.getAttribute('href');
      if (!linkHref || linkHref === '#' || linkHref === '' || linkHref.includes('javascript:')) {
        // Invalid or placeholder link
        invalidHandles.push(handle);
        return;
      }
      
      // Verify the link contains the product handle (allowing for query params and hash)
      // Check that the link path matches /products/{handle} or /products/{handle}/
      const linkPath = linkHref.split('?')[0].split('#')[0].trim();
      const expectedPath = `/products/${handle}`;
      const expectedPathWithSlash = `/products/${handle}/`;
      
      if (linkPath !== expectedPath && 
          linkPath !== expectedPathWithSlash && 
          !linkPath.endsWith(expectedPathWithSlash)) {
        // Link doesn't match the expected handle - might be placeholder/dummy data
        invalidHandles.push(handle);
        return;
      }

      try {
        container.insertAdjacentHTML('beforeend', html.trim());
      } catch (error) {
        console.warn('Failed to insert HTML for product:', handle, error);
        invalidHandles.push(handle);
      }
    });

    // Remove invalid handles from localStorage
    if (invalidHandles.length > 0) {
      this.removeHandles(invalidHandles);
    }

    // If no valid products were rendered, show empty message
    if (container.children.length === 0) {
      container.innerHTML = '<p class="recently-viewed-empty">No recently viewed products yet.</p>';
    }

    setTimeout(() => this.initializeSwiper(), 100);
  }

  clear() {
    this.destroySwiper();
    localStorage.removeItem(this.storageKey);
    const container = this.querySelector('.recently-viewed-products__content');
    if (container) container.innerHTML = '<p class="recently-viewed-empty">No recently viewed products yet.</p>';
  }

  forceReload() {
    this.renderRecentlyViewed();
  }

  refresh() {
    this.destroySwiper();
    
    if (window.Shopify && window.Shopify.designMode) {
      setTimeout(() => this.initializeSwiper(), 100);
      return;
    }
    
    const container = this.querySelector('.recently-viewed-products__content');
    if (container) {
      container.innerHTML = '';
      this.renderRecentlyViewed();
    }
  }

  handleResize() {
    const isMobile = window.innerWidth <= 749;
    const hasSwiperClass = this.querySelector('.recently-viewed-products__content.swiper');
    
    if (isMobile && !hasSwiperClass) {
      // Switch to mobile slider
      this.initializeSwiper();
    } else if (!isMobile && hasSwiperClass) {
      // Switch back to desktop grid
      this.destroySwiper();
    }
  }

  handleCustomizerUpdate(event) {
    // Check if this event is related to our section
    const sectionElement = event.target;
    const isOurSection = sectionElement && (
      sectionElement.querySelector('recently-viewed-products') ||
      sectionElement.tagName === 'RECENTLY-VIEWED-PRODUCTS' ||
      sectionElement.closest('recently-viewed-products')
    );
    
    if (isOurSection) {
      // Delay the refresh to ensure DOM is stable
      setTimeout(() => {
        try {
          this.refresh();
        } catch (error) {
          // Silently handle refresh errors
        }
      }, 100);
    }
  }
}

// Define custom element
customElements.define('recently-viewed-products', RecentlyViewedProducts);

// Initialize tracking when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductTracker();
  });
} else {
  // DOM is already ready
  new ProductTracker();
}